import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, Phone, Store, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Directory() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ city: "", state: "", q: "" });
  const [leadFor, setLeadFor] = useState(null);
  const [lead, setLead] = useState({ name: "", phone: "", message: "", city: "" });
  const [share, setShare] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/directory/public", { params: filters }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const sendLead = async (e) => {
    e.preventDefault();
    if (!share) {
      toast.error("Autorize o compartilhamento dos seus dados com o profissional.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/directory/public/${leadFor.slug}/lead`, { ...lead, accept_share: share });
      toast.success(data.message);
      setLeadFor(null);
      setLead({ name: "", phone: "", message: "", city: "" });
      setShare(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo light /></Link>
          <Link to="/cadastro">
            <Button className="bg-sky-600 hover:bg-sky-500 text-white h-11" data-testid="dir-cta-btn">Sou profissional — criar perfil</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="directory-page">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Diretório de profissionais de refrigeração</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Encontre técnicos e empresas de refrigeração da sua região. O GEPR facilita a descoberta e o contato —
          orçamento, preço, garantia e execução do serviço são de responsabilidade direta entre você e o profissional.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
            <Input className="h-12 pl-9" placeholder="Serviço ou especialidade..." data-testid="dir-q"
                   value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <Input className="h-12 sm:w-48" placeholder="Cidade" data-testid="dir-city"
                 value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
          <Input className="h-12 sm:w-24" placeholder="UF" maxLength={2} data-testid="dir-state"
                 value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} />
          <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={load} data-testid="dir-search-btn">Buscar</Button>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.length === 0 && (
            <Card className="border-slate-200 sm:col-span-2 lg:col-span-3">
              <CardContent className="p-10 text-center">
                <Store className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Nenhum profissional encontrado para esses filtros ainda.</p>
                <p className="text-slate-400 text-xs mt-1">O diretório está em lançamento — novos perfis são adicionados continuamente.</p>
              </CardContent>
            </Card>
          )}
          {items.map((p) => (
            <Card key={p.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`dir-card-${p.slug}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
                    <Store className="h-5 w-5 text-sky-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{p.trade_name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {p.city}{p.state && `/${p.state}`}
                    </p>
                  </div>
                </div>
                {p.specialties && <p className="mt-3 text-sm text-slate-600">{p.specialties}</p>}
                {p.service_types?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.service_types.slice(0, 4).map((s) => (
                      <span key={s} className="text-[11px] bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">{s}</span>
                    ))}
                  </div>
                )}
                {p.hours && <p className="mt-2 text-xs text-slate-400">{p.hours}</p>}
                <div className="mt-4 flex gap-2">
                  {p.whatsapp && (
                    <a href={`https://wa.me/55${String(p.whatsapp).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full h-11" data-testid={`dir-whats-${p.slug}`}>
                        <Phone className="h-4 w-4 mr-1" /> WhatsApp
                      </Button>
                    </a>
                  )}
                  <Dialog open={leadFor?.slug === p.slug} onOpenChange={(o) => setLeadFor(o ? p : null)}>
                    <DialogTrigger asChild>
                      <Button className="flex-1 h-11 bg-sky-600 hover:bg-sky-700 text-white" data-testid={`dir-contact-${p.slug}`}>Contatar</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader><DialogTitle>Contatar {p.trade_name}</DialogTitle></DialogHeader>
                      <form onSubmit={sendLead} className="space-y-3">
                        <div className="space-y-1.5"><Label>Seu nome *</Label><Input className="h-12" required data-testid="lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Seu telefone/WhatsApp *</Label><Input className="h-12" required data-testid="lead-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Sua cidade</Label><Input className="h-12" data-testid="lead-city" value={lead.city} onChange={(e) => setLead({ ...lead, city: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Descreva o que precisa</Label><Textarea rows={3} data-testid="lead-message" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} /></div>
                        <div className="flex items-start gap-2.5">
                          <Checkbox id="lead-share" checked={share} onCheckedChange={setShare} className="mt-0.5" data-testid="lead-share" />
                          <Label htmlFor="lead-share" className="text-xs text-slate-600 font-normal leading-snug">
                            Autorizo o compartilhamento dos meus dados de contato com este profissional, conforme a LGPD.
                          </Label>
                        </div>
                        <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="lead-send-btn">
                          {sending ? "Enviando..." : "Enviar contato"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-10 bg-white border border-slate-200 rounded-lg p-5 flex gap-3 text-sm text-slate-600" data-testid="dir-disclaimer">
          <Info className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
          <p>
            O GEPR apenas facilita a descoberta e o contato entre clientes e profissionais. Não garantimos qualidade,
            disponibilidade, preço ou execução do serviço. Contrato, orçamento, pagamento e garantia são de
            responsabilidade exclusiva das partes.
          </p>
        </div>
      </main>
    </div>
  );
}
